import { alpha, darken as muiDarken, lighten as muiLighten, rgbToHex } from '@material-ui/core/styles';

// MUI returns rgb() strings; solid tokens stay hex because some styles append an alpha suffix
// (`${colorPalette.green_90}d9`), which is invalid CSS on an rgb() value.
const lighten = (color, coefficient) => rgbToHex(muiLighten(color, coefficient)).toUpperCase();
const darken = (color, coefficient) => rgbToHex(muiDarken(color, coefficient)).toUpperCase();

// Builds the new look's palette for a brand from a few seed colors, as account-fe's brands do with
// ui-core's createColorPalette. Branch passes no seeds and keeps its hand-picked palette in variables.js.
// Token names stay Branch's (green_*, beige_*, orange_*) so every style file keeps working; for another
// brand they name a role, not a hue: green = dark text and titles, beige = surfaces, orange = the main action.
//
// Seeds:
//   primary    darkest: main buttons, body text, tooltips (Branch #0B1F15)
//   secondary  titles, icons, notices (Branch #527059)
//   accent     main action and selection controls (Branch #C93800)
//   surface    the panel shade the beige ramp is built around (Branch #F1ECE8)
//   error      (Branch #7A0000)
//   success    optional; Branch's success greens are kept when absent, so a red brand's
//              "saved" states don't turn red with its secondary color.
const BRANCH_SUCCESS = { green_success: '#527059', green_success_light: '#B4C7B8', green_success_dark: '#0B1F15' };

const successColors = (success) =>
  success
    ? { green_success: success, green_success_light: lighten(success, 0.55), green_success_dark: darken(success, 0.6) }
    : BRANCH_SUCCESS;

export const buildReskinColorPalette = ({ seeds, overrides = {} }) => {
  const { primary, secondary, accent, surface, error, success } = seeds;
  const mid = darken(secondary, 0.35);
  const beige25 = lighten(surface, 0.45);
  const beige40 = lighten(surface, 0.25);
  const beige60 = darken(surface, 0.08);
  const { green_success, green_success_light, green_success_dark } = successColors(success);

  return {
    white: '#FFFFFF',
    white_op_10: 'rgba(255, 255, 255, 0.10)',
    white_op_15: 'rgba(255, 255, 255, 0.15)',
    white_op_30: 'rgba(255, 255, 255, 0.3)',
    beige_25: beige25,
    beige_25_op_30: alpha(beige25, 0.3),
    beige_40: beige40,
    beige_50: surface,
    beige_50_op_13: alpha(surface, 0.13),
    beige_50_op_50: alpha(surface, 0.5),
    beige_50_op_60: alpha(surface, 0.6),
    beige_50_op_70: alpha(surface, 0.7),
    beige_50_op_75: alpha(surface, 0.75),
    beige_60: beige60,
    beige_60_op_30: alpha(beige60, 0.3),
    beige_60_op_55: alpha(beige60, 0.55),
    beige_60_op_80: alpha(beige60, 0.8),
    beige_75: darken(surface, 0.27),
    beige_90: darken(surface, 0.55),

    green_50: secondary,
    green_75: secondary,
    green_75_op_10: alpha(secondary, 0.1),
    green_75_op_30: alpha(secondary, 0.3),
    green_75_op_40: alpha(secondary, 0.4),
    green_75_op_60: alpha(secondary, 0.6),
    green_75_op_75: alpha(secondary, 0.75),
    green_75_10: lighten(secondary, 0.9),
    green_80: mid,
    green_90: mid,
    green_90_op_80: alpha(mid, 0.8),
    green_100: primary,

    orange_50: accent,
    orange_50_op_08: alpha(accent, 0.08),
    orange_50_op_10: alpha(accent, 0.1),
    orange_50_op_50: alpha(accent, 0.5),
    orange_50_op_70: alpha(accent, 0.7),
    orange_75: lighten(accent, 0.15),
    // Accent text on dark surfaces (green_75/green_90/green_100); the plain accent rarely has the contrast.
    orange_on_dark: lighten(accent, 0.75),
    orange_40: alpha(accent, 0.4),

    red_50: error,
    red_50_op_10: alpha(error, 0.1),
    red_100: error,
    red_100_10: lighten(error, 0.92),

    green_legacy_86c440: green_success,
    grey_10_op_38: alpha(mid, 0.38),

    green_success,
    green_success_light,
    green_success_dark,
    green_success_dark_op_90: alpha(green_success_dark, 0.9),
    red_error: error,
    red_error_light: lighten(error, 0.92),
    red_error_dark: darken(error, 0.5),
    red_error_dark_op_90: alpha(darken(error, 0.5), 0.9),
    black: '#000000',
    black_op_87: 'rgba(0, 0, 0, 0.87)',
    black_op_70: 'rgba(0, 0, 0, 0.70)',
    gray_page: beige25,
    gray_01: beige40,
    gray_02: beige25,
    gray_03: '#757774',
    gray_04: mid,

    // A brand can still pin any single token the seeds don't produce well.
    ...overrides
  };
};
